import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './SettingsEditor.css';
import './Button.css';
import {
  clamp,
  secondsToFrames,
  framesToSeconds,
  formatSeconds,
  getDefaultSettings,
} from '../utils/settingsControls';
import { trackEvent } from '../analytics';
import { diffSettingsForEvents } from '../analytics-helpers';
import { OPERATIONS, MOTION_MODES } from '../lib/operationCopy';

const TRACKED_SETTINGS_KEYS = [
  'motionThreshold',
  'min_motion_segment_length',
  'max_window_size',
  'min_motion_frames_in_window',
  'min_no_motion_segment_length',
];

const SettingHint = ({ text }) => {
  const [showPopup, setShowPopup] = useState(false);
  const iconRef = useRef(null);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPopup((prev) => !prev);
  };

  useEffect(() => {
    if (!showPopup) return;
    const handleOutside = (e) => {
      if (iconRef.current && !iconRef.current.contains(e.target)) {
        setShowPopup(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showPopup]);

  return (
    <span className="setting-hint-wrapper" ref={iconRef}>
      <button
        type="button"
        className="setting-hint-icon"
        onClick={handleClick}
        aria-label="More info"
        aria-expanded={showPopup}
      >
        ?
      </button>
      <span className="setting-hint-tooltip">{text}</span>
      {showPopup && (
        <div className="setting-hint-popup">{text}</div>
      )}
    </span>
  );
};

const framesLabel = (n) => `${n} ${n === 1 ? 'frame' : 'frames'}`;

const sliderFillStyle = (percent) => ({ '--fill': `${percent}%` });

const percentInRange = (value, min, max) => {
  if (max <= min) return 0;
  const p = ((value - min) / (max - min)) * 100;
  if (!Number.isFinite(p)) return 0;
  return Math.max(0, Math.min(100, p));
};

const SettingsEditor = ({
  settings,
  calculatedMotionThreshold,
  highestMotionValue,
  effectiveMinThreshold,
  effectiveMaxThreshold,
  fps,
  videoLengthSeconds,
  onRecalculate,
  motionAlgorithmVersion,
  motionValueCount,
  projectId,
  // #532's `motionPace` flag, only so this panel can agree with the selector
  // above it (#590). Dark — production today — the title is the pre-#532
  // "Trim stillness" string, byte for byte.
  motionPaceEnabled = false,
}) => {
  const [localSettings, setLocalSettings] = useState(settings || null);
  // Draft strings for numeric inputs so users can empty a field without the
  // panel snapping while they type. Committed values live in localSettings.
  const [drafts, setDrafts] = useState({});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const debounceTimeout = useRef(null);
  const advancedPanelRef = useRef(null);
  const lastEmittedRef = useRef(settings || null);

  useEffect(() => {
    if (advancedOpen && advancedPanelRef.current && typeof advancedPanelRef.current.scrollIntoView === 'function') {
      advancedPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [advancedOpen]);

  useEffect(() => {
    setLocalSettings(settings || null);
    setDrafts({});
    lastEmittedRef.current = settings || null;
  }, [settings, fps]);

  const debouncedRecalculate = useCallback((newSettings) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      const prev = lastEmittedRef.current;
      const events = diffSettingsForEvents(prev, newSettings, TRACKED_SETTINGS_KEYS);
      for (const evt of events) {
        trackEvent('setting_adjusted', { ...evt, project_id: projectId });
      }
      lastEmittedRef.current = newSettings;
      onRecalculate(newSettings);
    }, 500);
  }, [onRecalculate, projectId]);

  const commit = useCallback((patch) => {
    const next = { ...localSettings, ...patch };
    setLocalSettings(next);
    debouncedRecalculate(next);
  }, [localSettings, debouncedRecalculate]);

  const safeFps = Number.isFinite(fps) && fps > 0 ? fps : 30;
  const minClipMaxSeconds = Math.min(60, Number.isFinite(videoLengthSeconds) && videoLengthSeconds > 0 ? videoLengthSeconds : 60);

  const defaults = useMemo(
    () => getDefaultSettings(safeFps, calculatedMotionThreshold, {
      motionAlgorithmVersion,
      motionValueCount,
    }),
    [safeFps, calculatedMotionThreshold, motionAlgorithmVersion, motionValueCount]
  );

  const isAtDefaults = useMemo(() => {
    if (!localSettings) return true;
    const keys = Object.keys(defaults);
    return keys.every((k) => Number(localSettings[k]) === Number(defaults[k]));
  }, [localSettings, defaults]);

  const handleResetAll = () => {
    if (!localSettings) return;
    // Reset only the 5 tuning keys — transition/deflicker/output_fps/
    // clusteringAlgorithm are preserved (they belong to the render dialog).
    const next = { ...localSettings, ...defaults };
    setLocalSettings(next);
    setDrafts({});
    // Sync last-emitted so the pending debounced diff doesn't emit per-key
    // spam for the reset itself.
    lastEmittedRef.current = next;
    trackEvent('settings_reset', { project_id: projectId });
    // lastEmittedRef was just synced to `next`, so the debounced diff emits
    // no setting_adjusted events — only the delayed onRecalculate fires.
    debouncedRecalculate(next);
  };

  if (!localSettings) {
    return <div>Loading settings...</div>;
  }

  const sliderMin = effectiveMinThreshold != null ? effectiveMinThreshold : 0;
  const sliderMax = effectiveMaxThreshold != null ? effectiveMaxThreshold : highestMotionValue;
  const motionPercent = percentInRange(localSettings.motionThreshold, sliderMin, sliderMax);

  const minClipSeconds = framesToSeconds(localSettings.min_motion_segment_length ?? 0, safeFps);
  const minClipDraft = drafts.min_motion_segment_length;
  const minClipPercent = percentInRange(minClipSeconds, 0, minClipMaxSeconds);

  const windowSeconds = framesToSeconds(localSettings.max_window_size ?? 1, safeFps);
  const windowMin = framesToSeconds(1, safeFps);
  const windowPercent = percentInRange(windowSeconds, windowMin, 1);

  const requiredMotionSeconds = framesToSeconds(localSettings.min_motion_frames_in_window ?? 1, safeFps);
  const requiredMotionMax = windowSeconds;
  const requiredMotionPercent = percentInRange(requiredMotionSeconds, windowMin, requiredMotionMax);

  const bridgeSeconds = framesToSeconds(localSettings.min_no_motion_segment_length ?? 0, safeFps);
  const bridgeDraft = drafts.min_no_motion_segment_length;

  const handleThresholdChange = (e) => {
    const v = parseFloat(e.target.value);
    if (Number.isNaN(v)) return;
    commit({ motionThreshold: v });
  };

  const handleRevertThreshold = () => {
    commit({ motionThreshold: calculatedMotionThreshold });
  };

  const handleMinClipSlider = (e) => {
    const seconds = parseFloat(e.target.value);
    if (Number.isNaN(seconds)) return;
    const clamped = clamp(seconds, 0, minClipMaxSeconds);
    setDrafts((d) => ({ ...d, min_motion_segment_length: undefined }));
    commit({ min_motion_segment_length: secondsToFrames(clamped, safeFps) });
  };

  const handleMinClipInputChange = (e) => {
    const raw = e.target.value;
    setDrafts((d) => ({ ...d, min_motion_segment_length: raw }));
    if (raw === '' || raw === '-' || raw === '.') return;
    const seconds = parseFloat(raw);
    if (Number.isNaN(seconds)) return;
    const clamped = clamp(seconds, 0, minClipMaxSeconds);
    commit({ min_motion_segment_length: secondsToFrames(clamped, safeFps) });
  };

  const handleMinClipInputBlur = () => {
    setDrafts((d) => ({ ...d, min_motion_segment_length: undefined }));
    const current = framesToSeconds(localSettings.min_motion_segment_length ?? 0, safeFps);
    const clamped = clamp(current, 0, minClipMaxSeconds);
    if (clamped !== current) {
      commit({ min_motion_segment_length: secondsToFrames(clamped, safeFps) });
    }
  };

  const handleWindowSlider = (e) => {
    const seconds = parseFloat(e.target.value);
    if (Number.isNaN(seconds)) return;
    const clamped = clamp(seconds, windowMin, 1);
    const nextWindow = secondsToFrames(clamped, safeFps);
    const nextRequired = Math.min(localSettings.min_motion_frames_in_window ?? 1, nextWindow);
    commit({
      max_window_size: Math.max(1, nextWindow),
      min_motion_frames_in_window: Math.max(1, nextRequired),
    });
  };

  const handleRequiredMotionSlider = (e) => {
    const seconds = parseFloat(e.target.value);
    if (Number.isNaN(seconds)) return;
    const clamped = clamp(seconds, windowMin, windowSeconds);
    const nextRequired = secondsToFrames(clamped, safeFps);
    commit({
      min_motion_frames_in_window: Math.max(1, Math.min(nextRequired, localSettings.max_window_size ?? 1)),
    });
  };

  const handleBridgeChange = (e) => {
    const raw = e.target.value;
    setDrafts((d) => ({ ...d, min_no_motion_segment_length: raw }));
    if (raw === '' || raw === '-' || raw === '.') return;
    const seconds = parseFloat(raw);
    if (Number.isNaN(seconds)) return;
    const clamped = clamp(seconds, 0, 30);
    commit({ min_no_motion_segment_length: secondsToFrames(clamped, safeFps) });
  };

  const handleBridgeBlur = () => {
    setDrafts((d) => ({ ...d, min_no_motion_segment_length: undefined }));
    const current = framesToSeconds(localSettings.min_no_motion_segment_length ?? 0, safeFps);
    const clamped = clamp(current, 0, 30);
    if (clamped !== current) {
      commit({ min_no_motion_segment_length: secondsToFrames(clamped, safeFps) });
    }
  };

  return (
    <div className="settings-editor">
      <header className="settings-editor__header">
        <h4 className="settings-editor__title">
          {/* Operation name comes from the naming module (#379) verbatim — the
              inner controls below keep their motion vocabulary on purpose.

              WHICH name depends on the flag (#590). With `motionPace` lit this
              panel sits inside the Motion section, under a selector that calls
              this mode "MotionTrim" — two names for the same thing on one
              screen. The pair brand wins there, which is the rule
              operationCopy.js already states: any surface that pair-presents
              the two modes uses MOTION_MODES; standalone mentions keep the
              operation's own name. Dark, this is the standalone card and the
              string is unchanged. */}
          {motionPaceEnabled
            ? MOTION_MODES.trim.name
            : OPERATIONS.trim_stillness.displayName}
          {motionAlgorithmVersion === 2 && (
            <span
              className="settings-editor__engine-badge"
              title="This project was analyzed with the v2 motion engine."
              aria-label="Motion engine v2"
            >
              Motion engine v2
            </span>
          )}
        </h4>
        <p className="settings-editor__subtitle">Tuning is free — the preview below updates instantly.</p>
      </header>

      <div className="settings-editor__body">
        <div className="setting-group">
          <label htmlFor="motionThreshold">
            Motion Threshold
            <SettingHint text={'How much movement is needed to count as “motion.” Lower values keep more subtle activity.'} />
          </label>
          <div className="threshold-input-container">
            <input
              id="motionThreshold"
              type="range"
              min={sliderMin}
              max={sliderMax}
              step="0.01"
              name="motionThreshold"
              value={localSettings.motionThreshold}
              onChange={handleThresholdChange}
              className="setting-input"
              style={sliderFillStyle(motionPercent)}
            />
            <button
              type="button"
              onClick={handleRevertThreshold}
              className="btn-icon"
              title="Reset to default"
              aria-label="Reset motion threshold to default"
            >
              ↩
            </button>
          </div>
        </div>

        <div className="setting-group">
          <label htmlFor="minClipSecondsSlider">
            Minimum clip length
            <SettingHint text="The shortest burst of activity worth keeping. Anything shorter is treated as noise." />
          </label>
          <div className="dual-input-row">
            <input
              id="minClipSecondsSlider"
              type="range"
              min={0}
              max={minClipMaxSeconds}
              step={0.1}
              value={minClipSeconds}
              onChange={handleMinClipSlider}
              className="setting-input"
              style={sliderFillStyle(minClipPercent)}
              aria-label="Minimum clip length in seconds"
            />
            <div className="numeric-input">
              <input
                id="minClipSecondsNumber"
                type="number"
                min={0}
                max={minClipMaxSeconds}
                step={0.1}
                value={minClipDraft !== undefined ? minClipDraft : formatSeconds(minClipSeconds)}
                onChange={handleMinClipInputChange}
                onBlur={handleMinClipInputBlur}
                className="setting-input setting-input--compact"
                aria-label="Minimum clip length in seconds"
              />
              <span className="numeric-input__suffix">s</span>
            </div>
          </div>
        </div>

        <div className={`settings-editor__advanced${advancedOpen ? ' is-open' : ''}`}>
          <button
            type="button"
            className="settings-editor__advanced-toggle"
            onClick={() => setAdvancedOpen((v) => {
              if (!v) trackEvent('advanced_settings_opened', { project_id: projectId });
              return !v;
            })}
            aria-expanded={advancedOpen}
            aria-controls="settings-editor-advanced-panel"
          >
            <span className={`settings-editor__chevron${advancedOpen ? ' is-open' : ''}`} aria-hidden="true">›</span>
            Advanced
          </button>
          {advancedOpen && (
          <div
            id="settings-editor-advanced-panel"
            className="settings-editor__advanced-panel"
            ref={advancedPanelRef}
          >
            <div className="setting-group">
              <label htmlFor="smoothingWindowSlider">
                Smoothing window
                <SettingHint text="How long a stretch of frames the detector looks at together. Bigger windows smooth over brief pauses." />
              </label>
              <div className="dual-input-row">
                <input
                  id="smoothingWindowSlider"
                  type="range"
                  min={windowMin}
                  max={1}
                  step={1 / safeFps}
                  value={windowSeconds}
                  onChange={handleWindowSlider}
                  className="setting-input"
                  style={sliderFillStyle(windowPercent)}
                  aria-label="Smoothing window in seconds"
                />
                <span className="value-readout">{formatSeconds(windowSeconds)}s</span>
              </div>
              <p className="settings-editor__caption">= {framesLabel(localSettings.max_window_size)}</p>
            </div>

            <div className="setting-group">
              <label htmlFor="requiredMotionSlider">
                Required motion in window
                <SettingHint text="How much of that window must actually be moving for the section to count as activity." />
              </label>
              <div className="dual-input-row">
                <input
                  id="requiredMotionSlider"
                  type="range"
                  min={windowMin}
                  max={requiredMotionMax}
                  step={1 / safeFps}
                  value={requiredMotionSeconds}
                  onChange={handleRequiredMotionSlider}
                  className="setting-input"
                  style={sliderFillStyle(requiredMotionPercent)}
                  aria-label="Required motion in window in seconds"
                />
                <span className="value-readout">
                  {formatSeconds(requiredMotionSeconds)}s of {formatSeconds(windowSeconds)}s window
                </span>
              </div>
              <p className="settings-editor__caption">= {framesLabel(localSettings.min_motion_frames_in_window)}</p>
            </div>

            <div className="setting-group">
              <label htmlFor="bridgeStillnessInput">
                Bridge stillness gaps
                <SettingHint text="Quiet gaps shorter than this get merged into the surrounding motion, so nearby bursts stay in the same clip." />
              </label>
              <div className="numeric-input">
                <input
                  id="bridgeStillnessInput"
                  type="number"
                  min={0}
                  max={30}
                  step={0.1}
                  value={bridgeDraft !== undefined ? bridgeDraft : formatSeconds(bridgeSeconds)}
                  onChange={handleBridgeChange}
                  onBlur={handleBridgeBlur}
                  className="setting-input setting-input--compact"
                  aria-label="Bridge stillness gaps in seconds"
                />
                <span className="numeric-input__suffix">s</span>
              </div>
              <p className="settings-editor__caption">= {framesLabel(localSettings.min_no_motion_segment_length)}</p>
            </div>
          </div>
          )}
        </div>
      </div>
      <footer className="settings-editor__footer">
        <button
          type="button"
          className="settings-editor__reset"
          onClick={handleResetAll}
          disabled={isAtDefaults}
          title="Reset motion tuning to defaults"
        >
          <span aria-hidden="true">↩</span> Reset all
        </button>
      </footer>
    </div>
  );
};

export default SettingsEditor;
